import api from './api';

export interface UserProfile {
  id: number;
  full_name: string;
  email: string;
  phone: string;
  position: any;
  department: any;
  avatar?: string;
  role: any;
}

export interface NotificationPreferences {
  email_notifications: boolean;
  case_updates: boolean;
  task_reminders: boolean;
  billing_alerts: boolean;
  chat_messages: boolean;
  weekly_report: boolean;
}

export interface FirmSetting {
  id: number;
  key: string;
  value: string;
  created_at: string;
  updated_at: string;
  firm_id: number;
  description: string;
}

export interface FirmSettings {
  id?: number;
  name: string;
  slug?: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  state?: string | null;
  country: string;
  postal_code?: string | null;
  description?: string | null;
  logo_url?: string;
  website?: string | null;
  status?: string;
  subscription_plan?: string;
  subscription_expires_at?: string | null;
  settings?: FirmSetting[];
  created_at?: string;
  updated_at?: string;
  deleted_at?: string | null;
  default_currency: string;
  tax_rate: number;
  company_name?: string | null;
  company_address?: string | null;
  invoice_prefix?: string;
  invoice_next_number?: number;
  enable_payment_reminders?: boolean;
  bank_details: string;
  bill_footer: string;
}

export interface ChangePasswordInput {
  current_password: string;
  new_password: string;
}

const settingsService = {
  // --- Profile ---
  getProfile: async (userId: string) => {
    const response = await api.get(`/settings/profile/${userId}`);
    return response.data;
  },

  updateProfile: async (userId: string, data: Partial<UserProfile>) => {
    const response = await api.put(`/settings/profile/${userId}`, data);
    return response.data;
  },

  uploadAvatar: async (userId: string, file: FormData) => {
    const response = await api.post(`/settings/profile/${userId}/avatar`, file, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  // --- Notifications ---
  getNotificationPreferences: async (userId: string) => {
    const response = await api.get(`/settings/notifications/${userId}`);
    return response.data;
  },

  updateNotificationPreferences: async (userId: string, data: NotificationPreferences) => {
    const response = await api.put(`/settings/notifications/${userId}`, data);
    return response.data;
  },

  // --- Security ---
  changePassword: async (userId: string, data: ChangePasswordInput) => {
    const response = await api.put(`/settings/security/${userId}/password`, data);
    return response.data;
  },

  enable2FA: async (userId: string) => {
    const response = await api.post(`/settings/security/${userId}/2fa/enable`);
    return response.data;
  },

  disable2FA: async (userId: string) => {
    const response = await api.post(`/settings/security/${userId}/2fa/disable`);
    return response.data;
  },

  verify2FA: async (userId: string, code: string) => {
    const response = await api.post(`/settings/security/${userId}/2fa/verify`, { code });
    return response.data;
  },

  // --- Firm ---
  getFirmSettings: async () => {
    const response = await api.get('/settings/firm');
    return response.data;
  },

  updateFirmSettings: async (data: Partial<FirmSettings>) => {
    const response = await api.put('/settings/firm', data);
    return response.data;
  },

  uploadFirmLogo: async (file: FormData) => {
    const response = await api.post('/settings/firm/logo', file, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
};

export default settingsService;
